import { useState } from 'react'
import { useT } from '../i18n'
import { flagEmoji, flagLabel, isFlagOn, splitFlagList, type FlagCount } from './flagFilter'

/**
 * Выбор флагов иностранного флота.
 *
 * Кнопка «Ин.флот» показывает всё сразу, а в рабочем районе это тысячи судов —
 * картина перестаёт читаться (фидбек владельца 2026-08-07). Список НЕ зашит:
 * приходит с ответом слоя по текущему кадру, со счётчиками. Пустой выбор =
 * все флаги: выключенный фильтр не должен гасить слой.
 */
export function FlagPicker({
  counts,
  selected,
  onToggle,
}: {
  counts: FlagCount[]
  selected: string[]
  onToggle: (code: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const t = useT()
  if (!counts.length) return null
  const { top, rest } = splitFlagList(counts, selected)
  const shown = expanded ? [...top, ...rest] : top

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-1">
        {shown.map(f => {
          const on = isFlagOn(selected, f.code)
          const emoji = flagEmoji(f.code)
          return (
            <button
              key={f.code}
              type="button"
              role="checkbox"
              aria-checked={on}
              aria-label={flagLabel(f.code)}
              title={flagLabel(f.code)}
              onClick={() => onToggle(f.code)}
              className={
                'rounded-md border px-1.5 py-0.5 text-[11px] transition-colors ' +
                (on
                  ? 'border-sonar bg-sonar/15 text-foam'
                  : 'border-hull text-mist opacity-50 hover:border-sonar hover:opacity-100')
              }
            >
              {/* Флаг страны — картинкой, название уходит в подсказку: чипы со
                  словами занимали три строки панели. Незнакомый код показываем
                  как есть — чужой флаг рисовать нельзя. */}
              <span className="mr-0.5">{emoji ?? flagLabel(f.code)}</span>
              {/* Счётчик — то, ради чего список строится по кадру: видно, где
                  флот есть, а где одна точка. */}
              <span className="tnum ml-1 text-faint">{f.count}</span>
            </button>
          )
        })}
      </div>
      {rest.length > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="text-[11px] text-mist transition-colors hover:text-foam"
        >
          {expanded ? t('common.collapse') : t('common.moreCount', { count: rest.length })}
        </button>
      )}
      {selected.length > 0 && (
        <div className="text-[10px] text-faint">
          {t('models.flags.someOff')}
        </div>
      )}
    </div>
  )
}
